import { useState, useEffect } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { Radio, Space, Checkbox } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
    getClient,
    updateClient,
    createClientDataStore,
    getClientDataStore,
    deleteClientFieldFromDataStore,
    deleteClientLogoByCountryCode,
    deleteClientLogoThumbByCountryCode,
    deleteClientFaviconByCountryCode,
    deleteClientEmailLogoByCountryCode,
} from '../../../../../redux/slices/clientsSlice';
import { toast } from 'react-toastify';
import {
    DetailsGeneralInputs,
    DetailsUrlsInputs,
    WhiteLabelInputs,
} from './DeatailsInputs';
import MultinationalTabs from '../../../../../components/MultinationalTabs/MultinationalTabs';
import DetailsCompanyInfoInputs from './DeatailsInputs/DetailsCompanyInfoInputs';
import { allThemes, getThemes } from '../../../../../redux/slices/themeSlice';
import { CriiptoMethods } from '../../../../../components/CriiptoMethods';
import {
    createShallowCopy,
    getClientFormData,
} from '../../../../../utils/helpFunctions';
import { countryCodesMap } from '../../../../../shared/data/countryCodesMap';
import useClientDataState from './hooks/useClientDataState';
import useDataStoreState from './hooks/useDataStoreState';
import useLogosState from './hooks/useLogosState';

const REGEXP_ACCEPT_IMAGE = /(image\/svg\+xml|image\/png|image\/jpe?g)/i;

export default function DetailsForm({
    client,
    generalData,
    setView,
    termsAndPrivacy,
    excludedCodes,
}) {
    const dispatch = useDispatch();
    const params = useParams();

    const themes = useSelector(allThemes);

    const [radioButtonValue, setRadioButtonValue] = useState(
        (generalData?.email_required && 'email') ||
            (generalData?.ssn_required && 'ssn') ||
            null,
    );

    const [selectedIntegration, setSelectedIntegration] = useState(
        (generalData?.is_bank_id_login_enabled && 'bankId') ||
            (generalData?.is_criipto_integration_enabled && 'criipto') ||
            'noIntegration',
    );

    const [selectedLoginButtonsStyles, setSelectedLoginButtonsStyles] =
        useState(generalData?.criipto_buttons_styles ?? 'default');

    const [data, setData] = useClientDataState({
        client,
        termsAndPrivacy,
        generalData,
    });

    const [dataStore, setDataStore] = useDataStoreState({
        generalData,
        client,
    });

    const [logos, logoErrors, setLogos, setLogoErrors] = useLogosState({
        generalData,
    });

    const [errors, setErrors] = useState({});

    const [selectOptionsThemes, setSelectOptionsThemes] = useState([]);

    const selectedCriiptoIntegration = selectedIntegration === 'criipto';

    useEffect(() => {
        dispatch(getThemes());
    }, [dispatch]);

    useEffect(() => {
        if (themes) {
            const selectOptions = themes?.reduce((acc, el) => {
                acc.push({
                    id: el.id,
                    value: el.name.toLowerCase(),
                    label: el.name,
                    key: 'theme_id',
                });

                return acc;
            }, []);
            setSelectOptionsThemes(selectOptions);
        }
    }, [themes]);

    const onChange = (e) => {
        const value = e.target.value;
        const fieldId = e.target.id;
        setData({
            ...data,
            [fieldId]: value,
        });
        if (fieldId === 'api_url') {
            setDataStore({ ...dataStore, api_url: value });
        }
    };

    const onChangeColors = (e, { head, color }) => {
        const newData = JSON.parse(JSON.stringify(data));
        const value = typeof e === 'boolean' ? e : e.target.value;

        newData.white_label_colors[head][color] = value;

        setData(newData);
    };

    const onChangeMultinational = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const newData = {
            ...data,
            countries_localizations: {
                ...data.countries_localizations,
                [flag]: {
                    ...data.countries_localizations[flag],
                    [currentInput]: value,
                },
            },
        };

        setData(newData);
    };

    const onChangeMultinationalDataStore = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const newData = {
            ...dataStore,
            countries_localizations: {
                ...dataStore.countries_localizations,
                [flag]: {
                    ...dataStore.countries_localizations[flag],
                    [currentInput]: value,
                },
            },
        };

        setDataStore(newData);
    };
    const onRadioButtonsChange = (e) => {
        setRadioButtonValue(e.target.value);
        setDataStore({
            ...dataStore,

            email_required: e.target.value === 'email' ? true : false,
            ssn_required: e.target.value === 'ssn' ? true : false,
        });
    };

    const onCheckboxChange = (e, fieldName) => {
        setData({
            ...data,
            [fieldName]: e.target.checked ? 1 : 0,
        });
    };

    const onCheckboxChangeDataStore = (e) => {
        const { name, checked } = e.target;
        setErrors({ ...errors, is_login_email_form_enabled: null });
        setDataStore({
            ...dataStore,
            [name]: checked,
        });
    };

    const onClientEdit = async (e) => {
        e.preventDefault();

        const requiredFieldErrors = validateForm();
        setErrors({
            ...errors,
            ...requiredFieldErrors,
        });

        if (Object.keys(requiredFieldErrors).length > 0) {
            return;
        }

        const themeName = themes?.find((el) => el.id === data.theme_id)?.name;
        const fields = [];

        if (data?.criipto_methods && selectedCriiptoIntegration) {
            fields.push({
                key_name: 'criipto_methods',
                value: data.criipto_methods,
            });
        }

        if (
            !selectedCriiptoIntegration &&
            generalData &&
            generalData.criipto_methods &&
            Object.values(generalData.criipto_methods).some(
                (methods) => methods && methods.length,
            )
        ) {
            dispatch(
                deleteClientFieldFromDataStore({
                    id: client.client_uid,
                    collectionName: 'clients',
                    fieldName: 'criipto_methods',
                }),
            );
        }
        if (themeName) {
            fields.push({ key_name: 'theme', value: themeName });
        }
        fields.push({
            key_name: 'white_label_colors',
            value: data.white_label_colors,
        });
        Object.keys(dataStore).forEach((key) => {
            const value = dataStore[key];
            if (value !== undefined) {
                fields.push({ key_name: key, value });
            }
        });
        const bodyDataStore = { fields };
        try {
            await dispatch(
                updateClient({
                    body: getClientFormData(logos, data),
                    id: params.id,
                }),
            ).unwrap();

            if (Object.keys(bodyDataStore.fields).length !== 0) {
                await dispatch(
                    createClientDataStore({
                        id: client.client_uid,
                        body: bodyDataStore,
                        collectionName: 'clients',
                    }),
                ).unwrap();
                await dispatch(
                    getClientDataStore({
                        id: client.client_uid,
                        collectionName: 'clients',
                    }),
                ).unwrap();
            }
            setView('view');
            dispatch(getClient(params.id));
            toast.success('Changes saved successfully');
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onFileSelect = (e, flag) => {
        const loaderName = e.target.name;

        if (e.target.files && e.target.files[0]) {
            if (!REGEXP_ACCEPT_IMAGE.test(e.target.files[0].type)) {
                if (loaderName === 'big-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            bigLogoErrors: {
                                ...prev[flag].bigLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'small-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            smallLogoErrors: {
                                ...prev[flag].smallLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'favicon') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            faviconErrors: {
                                ...prev[flag].faviconErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'email-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            emailLogoErrors: {
                                ...prev[flag].emailLogoErrors,
                                isWrongFileType: true,
                            },
                        },
                    }));
                }
                return;
            }

            if (e.target.files[0].size > 2097152) {
                if (loaderName === 'big-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            bigLogoErrors: {
                                ...prev[flag].bigLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'small-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            smallLogoErrors: {
                                ...prev[flag].smallLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'favicon') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            faviconErrors: {
                                ...prev[flag].faviconErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                if (loaderName === 'email-logo') {
                    setLogoErrors((prev) => ({
                        ...prev,
                        [flag]: {
                            ...prev[flag],
                            emailLogoErrors: {
                                ...prev[flag].emailLogoErrors,
                                isFileOversized: true,
                            },
                        },
                    }));
                }
                return;
            }

            setLogoErrors((prev) => ({
                ...prev,
                [flag]: {
                    ...prev[flag],
                    ...(loaderName === 'big-logo' && {
                        bigLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...(loaderName === 'small-logo' && {
                        smallLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...(loaderName === 'favicon' && {
                        faviconErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                    ...(loaderName === 'email-logo' && {
                        emailLogoErrors: {
                            isWrongFileType: false,
                            isFileOversized: false,
                        },
                    }),
                },
            }));

            const bigLogoUrl = window.URL.createObjectURL(e.target.files[0]);
            const smallLogoUrl = window.URL.createObjectURL(e.target.files[0]);
            const faviconUrl = window.URL.createObjectURL(e.target.files[0]);
            const emailLogoUrl = window.URL.createObjectURL(e.target.files[0]);

            const file = e.target.files[0];

            if (loaderName === 'big-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        bigLogo: {
                            logoUrl: bigLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'small-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        smallLogo: {
                            logoUrl: smallLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'favicon') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        favicon: {
                            logoUrl: faviconUrl,
                            logoData: file,
                        },
                    },
                }));
            }
            if (loaderName === 'email-logo') {
                setLogos((prev) => ({
                    ...prev,
                    [flag]: {
                        ...prev[flag],
                        emailLogo: {
                            logoUrl: emailLogoUrl,
                            logoData: file,
                        },
                    },
                }));
            }
        }
    };

    const onDeleteLogo = async (e, flag) => {
        e.preventDefault();
        const currentButtonName = e.currentTarget.name;

        if (currentButtonName === 'big-logo') {
            await dispatch(
                deleteClientLogoByCountryCode({
                    id: client?.id,
                    countryCode: countryCodesMap[flag],
                }),
            ).unwrap();
            toast.success('Logo successfully deleted');
        }

        if (currentButtonName === 'small-logo') {
            await dispatch(
                deleteClientLogoThumbByCountryCode({
                    id: client?.id,
                    countryCode: countryCodesMap[flag],
                }),
            ).unwrap();
            toast.success('Mark successfully deleted');
        }

        if (currentButtonName === 'favicon') {
            await dispatch(
                deleteClientFaviconByCountryCode({
                    id: client?.id,
                    countryCode: countryCodesMap[flag],
                }),
            ).unwrap();
            toast.success('Favicon successfully deleted');
        }

        if (currentButtonName === 'email-logo') {
            await dispatch(
                deleteClientEmailLogoByCountryCode({
                    id: client?.id,
                    countryCode: countryCodesMap[flag],
                }),
            ).unwrap();
            toast.success('Email template logo successfully deleted');
        }

        setLogos((prev) => ({
            ...prev,
            [flag]: {
                ...prev[flag],
                ...(currentButtonName === 'big-logo' && {
                    bigLogo: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'small-logo' && {
                    smallLogo: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'favicon' && {
                    favicon: { logoUrl: '', logoData: null },
                }),
                ...(currentButtonName === 'email-logo' && {
                    emailLogo: { logoUrl: '', logoData: null },
                }),
            },
        }));
    };

    const onThemeChange = (option) => {
        setData({ ...data, [option.key]: option.id });
    };

    const onSelectedIntegration = (e) => {
        const value = e.target.value;

        setSelectedIntegration(value);
        setErrors({
            ...errors,
            criipto_methods: null,
            is_login_email_form_enabled: null,
        });

        if (value === 'bankId') {
            setDataStore({
                ...dataStore,
                is_bank_id_login_enabled: true,
                is_criipto_integration_enabled: false,
            });
            setData({ ...data, criipto_methods: [] });
            return;
        }

        if (value === 'criipto') {
            setDataStore({
                ...dataStore,
                is_bank_id_login_enabled: false,
                is_criipto_integration_enabled: true,
            });

            return;
        }

        setDataStore({
            ...dataStore,
            is_bank_id_login_enabled: false,
            is_criipto_integration_enabled: false,
        });
        setData({ ...data, criipto_methods: [] });
    };

    const onSelectLoginButtonsStyles = (option) => {
        setSelectedLoginButtonsStyles(option.value);

        setDataStore({
            ...dataStore,
            criipto_buttons_styles: option.value,
        });
    };

    const validateForm = () => {
        const requiredErrors = {};

        if (
            Object.values(data?.criipto_methods || {}).every(
                (methods) => !methods.length,
            ) &&
            selectedCriiptoIntegration
        ) {
            const errorText = 'Please select at least one method.';
            requiredErrors.criipto_methods = [errorText];
            toast.error(errorText);
        }

        if (
            selectedIntegration === 'noIntegration' &&
            !dataStore.is_login_email_form_enabled
        ) {
            const errorText = 'Please select at least one login method.';
            requiredErrors.is_login_email_form_enabled = [errorText];
            toast.error(errorText);
        }

        if (!dataStore.account_types.length) {
            const errorText = 'Please select at least one account type.';
            requiredErrors.account_types = [errorText];
            toast.error(errorText);
        }

        return requiredErrors;
    };

    const handleCriiptoMethods = (methods) => {
        setData({ ...data, criipto_methods: methods });
        setErrors({ ...errors, criipto_methods: null });
    };

    const onChangeCompanyInfoDataStore = (e, flag) => {
        const currentInput = e.target.id.slice(0, e.target.id.length - 3);
        const value = e.target.value;
        const dataStoreCopy = createShallowCopy(dataStore);

        dataStoreCopy.company_info[flag][currentInput] = value;
        setDataStore(dataStoreCopy);
    };

    const onLogosSizesChange = (e, flag) => {
        const [logoType, parameter] = e.target.id.split('_');
        const value = e.target.value;
        const dataStoreCopy = createShallowCopy(dataStore);

        dataStoreCopy.logos_sizes.web_app[logoType][countryCodesMap[flag]][
            parameter
        ] = value;

        setDataStore(dataStoreCopy);
    };

    return (
        <form id="edit-client-form" onSubmit={onClientEdit}>
            <div className="details-table">
                <DetailsGeneralInputs
                    onChange={onChange}
                    data={data}
                    errors={errors}
                />
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Required
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <Radio.Group
                                    onChange={onRadioButtonsChange}
                                    value={radioButtonValue}
                                >
                                    <Space direction="horizontal">
                                        <Radio value="email">Email</Radio>
                                        <Radio value="ssn">SSN</Radio>
                                    </Space>
                                </Radio.Group>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Choose integration
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <Radio.Group
                                    onChange={onSelectedIntegration}
                                    value={selectedIntegration}
                                >
                                    <Space direction="horizontal">
                                        <Radio value="criipto">Criipto</Radio>
                                        {/* <Radio value="bankId">BankId</Radio> */}
                                        <Radio value="noIntegration">
                                            No integration
                                        </Radio>
                                    </Space>
                                </Radio.Group>
                            </div>
                        </Col>
                    </Row>
                </div>
                {selectedCriiptoIntegration && (
                    <div className="details-table__row">
                        <Row>
                            <Col span={7}>
                                <div className="details-table__row-head details-table__cell">
                                    SSN verification only
                                </div>
                            </Col>
                            <Col span={17}>
                                <div className="details-table__cell">
                                    <div>
                                        <Form.Item
                                            validateStatus={
                                                errors?.ssn_verification_only
                                                    ? 'error'
                                                    : ''
                                            }
                                            help={
                                                errors?.ssn_verification_only
                                                    ? errors?.ssn_verification_only
                                                    : ''
                                            }
                                            style={{ margin: 0 }}
                                        >
                                            <div style={{ width: '50px' }}>
                                                <Checkbox
                                                    onChange={
                                                        onCheckboxChangeDataStore
                                                    }
                                                    name="ssn_verification_only"
                                                    checked={
                                                        dataStore?.ssn_verification_only
                                                    }
                                                >
                                                    Yes
                                                </Checkbox>
                                            </div>
                                        </Form.Item>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}
                {selectedCriiptoIntegration && (
                    <div className="details-table__row">
                        <Row>
                            <Col span={24}>
                                <div>
                                    <CriiptoMethods
                                        methods={generalData?.criipto_methods}
                                        errors={errors}
                                        handleCriiptoMethods={
                                            handleCriiptoMethods
                                        }
                                        editMode
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Enable email form for login
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <div>
                                    <Form.Item
                                        validateStatus={
                                            errors?.is_login_email_form_enabled
                                                ? 'error'
                                                : ''
                                        }
                                        help={
                                            errors?.is_login_email_form_enabled
                                                ? errors?.is_login_email_form_enabled
                                                : ''
                                        }
                                        style={{ margin: 0 }}
                                    >
                                        <div style={{ width: '50px' }}>
                                            <Checkbox
                                                onChange={
                                                    onCheckboxChangeDataStore
                                                }
                                                name="is_login_email_form_enabled"
                                                checked={
                                                    dataStore?.is_login_email_form_enabled
                                                }
                                            >
                                                Yes
                                            </Checkbox>
                                        </div>
                                    </Form.Item>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Enable 2factor auth for login
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <div>
                                    <Form.Item
                                        validateStatus={
                                            errors?.is_2fa_enabled
                                                ? 'error'
                                                : ''
                                        }
                                        help={
                                            errors?.is_2fa_enabled
                                                ? errors?.is_2fa_enabled
                                                : ''
                                        }
                                        style={{ margin: 0 }}
                                    >
                                        <div style={{ width: '50px' }}>
                                            <Checkbox
                                                onChange={
                                                    onCheckboxChangeDataStore
                                                }
                                                name="is_2fa_enabled"
                                                checked={
                                                    dataStore?.is_2fa_enabled
                                                }
                                            >
                                                Yes
                                            </Checkbox>
                                        </div>
                                    </Form.Item>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Send SMS if phone number is not confirmed
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <div style={{ width: '50px' }}>
                                    <Checkbox
                                        onChange={(e) =>
                                            onCheckboxChange(
                                                e,
                                                'is_send_sms_if_phone_not_confirmed',
                                            )
                                        }
                                        checked={
                                            data?.is_send_sms_if_phone_not_confirmed
                                        }
                                    >
                                        Yes
                                    </Checkbox>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="details-table__row">
                    <Row>
                        <Col span={7}>
                            <div className="details-table__row-head details-table__cell">
                                Show Powered by Defentry logo
                            </div>
                        </Col>
                        <Col span={17}>
                            <div className="details-table__cell">
                                <div style={{ width: '50px' }}>
                                    <Checkbox
                                        onChange={onCheckboxChangeDataStore}
                                        name="is_show_powered_by_defentry_logo"
                                        checked={
                                            dataStore?.is_show_powered_by_defentry_logo
                                        }
                                    >
                                        Yes
                                    </Checkbox>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
            <h2 className="table-top-pannel__title table-top-pannel__title--indent-bottom-24px ">
                White label
            </h2>
            <WhiteLabelInputs
                data={data.white_label_colors}
                theme={data.theme_id}
                onChangeColors={onChangeColors}
                onThemeChange={onThemeChange}
                selectLoginButtonsStyles={onSelectLoginButtonsStyles}
                selectedLoginButtonsStyles={selectedLoginButtonsStyles}
                selectOptionsThemes={selectOptionsThemes}
                errors={errors}
            />
            <h2 className="table-top-pannel__title table-top-pannel__title--indent-bottom-24px ">
                Company info
            </h2>
            <MultinationalTabs
                codeType="countries"
                excludedCodes={excludedCodes}
            >
                <DetailsCompanyInfoInputs
                    onChange={onChangeMultinational}
                    onChangeCompanyInfo={onChangeCompanyInfoDataStore}
                    data={dataStore.company_info}
                    errors={errors}
                    onFileSelect={onFileSelect}
                    logos={logos}
                    logoErrors={logoErrors}
                    dataStore={dataStore}
                    onDeleteLogo={onDeleteLogo}
                    onLogosSizesChange={onLogosSizesChange}
                />
            </MultinationalTabs>
            <h2 className="table-top-pannel__title table-top-pannel__title--indent-bottom-24px ">
                URLs
            </h2>
            <MultinationalTabs
                codeType="countries"
                excludedCodes={excludedCodes}
            >
                <DetailsUrlsInputs
                    onChange={onChangeMultinationalDataStore}
                    data={dataStore.countries_localizations}
                    errors={errors}
                />
            </MultinationalTabs>
        </form>
    );
}
